import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import { DateTime } from "luxon";
import { EventDate } from "../../../../../../../../shared/components/cards/card-manage-event/types/event-date.type";
import { TicketBatch } from "../../../../../../../../shared/components/modal-tickets/types/ticket-batch.type";
import { batchHasPaidSales } from "../../../../../../../../shared/helpers/ticket-sales-lock.helper";

type TicketsDateRangeValidatorOptions = {
  validateNotBeforeNow: boolean;
  ticketBatches?: TicketBatch[];
  selectedBatch?: TicketBatch; // se estiver editando, ignora esse no overlap
};

/**
 * Espera que o form tenha:
 * - startDateTime: Date | string(datetime-local)
 * - endDateTime: Date | string(datetime-local)
 */
export function ticketsDateRangeValidatorDateTime(
  eventDate: EventDate,
  options: TicketsDateRangeValidatorOptions,
): ValidatorFn {
  return (form: AbstractControl): ValidationErrors | null => {
    const startDateTime = normalizeToLuxonDateTime(
      form.get("startDateTime")?.value,
    );
    const endDateTime = normalizeToLuxonDateTime(
      form.get("endDateTime")?.value,
    );
    const eventEndDateTime = buildEventEndDateTime(eventDate);

    if (!startDateTime || !endDateTime || !eventEndDateTime) {
      return null;
    }

    if (startDateTime >= eventEndDateTime) {
      return { startDateAfterEventEnd: true };
    }

    const selected = options.selectedBatch;
    const originalStart = normalizeToLuxonDateTime(selected?.sale_start);
    const originalEnd = normalizeToLuxonDateTime(selected?.sale_end);
    if (
      selected &&
      batchHasPaidSales(selected) &&
      originalStart &&
      originalStart.startOf("minute").toMillis() !==
        startDateTime.startOf("minute").toMillis()
    ) {
      return { openingLockedAfterSales: true };
    }
    const closingChanged =
      !!selected?.id &&
      (!originalEnd ||
        originalEnd.startOf("minute").toMillis() !==
          endDateTime.startOf("minute").toMillis());
    if (selected && closingChanged && originalEnd) {
      if (
        selected.transition_closed_at ||
        selected.on_sale === false ||
        originalEnd.toMillis() <= Date.now() ||
        (!selected.on_sale &&
          (!originalStart || originalStart.toMillis() > Date.now()))
      ) {
        return { onlyCurrentBatchCanPostpone: true };
      }
      if (endDateTime < originalEnd) return { closingMustBePostponed: true };
    }
    const eventStart = normalizeToLuxonDateTime(
      `${eventDate.startDate}T${eventDate.startTime}`,
    );
    if (eventStart && startDateTime < eventStart && endDateTime > eventStart)
      return { closingAfterEventStart: true };

    if (startDateTime.toMillis() === endDateTime.toMillis()) {
      return { datesAreEqual: true };
    }

    if (startDateTime > endDateTime) {
      return { dateEndBeforeStart: true };
    }

    if (options.validateNotBeforeNow) {
      const now = DateTime.local();
      if (startDateTime < now || endDateTime < now) {
        return { dateBeforeCurrent: true };
      }
    }

    if (eventEndDateTime < endDateTime) {
      return { endDateAfterEventEnd: true };
    }

    const overlapError = validateNoOverlapWithOtherBatches({
      startDateTime,
      endDateTime,
      ticketBatches: options.ticketBatches ?? [],
      selectedBatch: options.selectedBatch,
    });

    if (overlapError) {
      return overlapError;
    }

    return null;
  };
}

function validateNoOverlapWithOtherBatches(params: {
  startDateTime: DateTime;
  endDateTime: DateTime;
  ticketBatches: TicketBatch[];
  selectedBatch?: TicketBatch;
}): ValidationErrors | null {
  const { startDateTime, endDateTime, ticketBatches, selectedBatch } = params;

  for (const ticketBatch of ticketBatches) {
    if (
      ticketBatch.transition_closed_at ||
      ticketBatch.transition_mode === "sold_out_only"
    )
      continue;
    if (!ticketBatch?.sale_start || !ticketBatch?.sale_end) continue;

    // Ignora o próprio lote quando estiver editando
    if (
      selectedBatch &&
      (ticketBatch === selectedBatch ||
        (selectedBatch.id !== undefined && ticketBatch.id === selectedBatch.id))
    ) {
      continue;
    }

    const batchStart = DateTime.fromISO(ticketBatch.sale_start);
    const batchEnd = DateTime.fromISO(ticketBatch.sale_end);

    if (!batchStart.isValid || !batchEnd.isValid) continue;

    // overlap se: start < otherEnd && end > otherStart
    const hasOverlap = startDateTime < batchEnd && endDateTime > batchStart;

    if (hasOverlap) {
      return { batchDateOverlapsAnotherBatch: true };
    }
  }

  return null;
}

function normalizeToLuxonDateTime(value: unknown): DateTime | null {
  if (!value) return null;

  if (value instanceof Date) {
    const dateTime = DateTime.fromJSDate(value);
    return dateTime.isValid ? dateTime : null;
  }

  if (typeof value === "string") {
    const dateTime = DateTime.fromISO(value);
    return dateTime.isValid ? dateTime : null;
  }

  return null;
}

function buildEventEndDateTime(eventDate: EventDate): DateTime | null {
  if (!eventDate?.endDate || !eventDate?.endTime) return null;

  const isoCandidate = `${eventDate.endDate}T${eventDate.endTime}`;
  const dateTime = DateTime.fromISO(isoCandidate);

  return dateTime.isValid ? dateTime : null;
}
